import { useNavigate } from 'react-router-dom';

interface SessionCardProps {
  session: {
    id: string;
    name: string;
    numPlayers: number;
    status: 'DRAFT' | 'SETUP' | 'ACTIVE' | 'COMPLETED';
    createdAt: string;
    startedAt?: string;
    endedAt?: string;
    stats?: {
      totalAudience: number;
      charactersCount: number;
      votesCount: number;
      challengesCount: number;
    };
  };
  onActivate: (sessionId: string) => void;
  onClose: (sessionId: string) => void;
  onDelete: (sessionId: string) => void;
}

export function SessionCard({ session, onActivate, onClose, onDelete }: SessionCardProps) {
  const navigate = useNavigate();
  const statusColors = {
    DRAFT: 'text-gray-400 bg-gray-700',
    SETUP: 'text-yellow-400 bg-yellow-900 bg-opacity-30',
    ACTIVE: 'text-green-400 bg-green-900 bg-opacity-30',
    COMPLETED: 'text-blue-400 bg-blue-900 bg-opacity-30',
  };

  const statusIcons = {
    DRAFT: '📝',
    SETUP: '⚙️',
    ACTIVE: '🎭',
    COMPLETED: '✅',
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="bg-gray-800 rounded-lg p-6 border border-gray-700 hover:border-gray-600 transition">
      {/* Header */}
      <div className="flex justify-between items-start mb-4">
        <div className="flex-1">
          <h3 className="text-xl font-bold text-white mb-2">{session.name}</h3>
          <div className={`inline-block px-3 py-1 rounded-full text-sm font-semibold ${statusColors[session.status]}`}>
            {statusIcons[session.status]} {session.status}
          </div>
        </div>
      </div>

      {/* Info */}
      <div className="space-y-2 mb-4">
        <div className="text-sm">
          <span className="text-gray-400">Players:</span>
          <span className="text-white ml-2 font-semibold">{session.numPlayers}</span>
        </div>
        <div className="text-sm">
          <span className="text-gray-400">Created:</span>
          <span className="text-white ml-2">{formatDate(session.createdAt)}</span>
        </div>
        {session.startedAt && (
          <div className="text-sm">
            <span className="text-gray-400">Started:</span>
            <span className="text-white ml-2">{formatDate(session.startedAt)}</span>
          </div>
        )}
        {session.endedAt && (
          <div className="text-sm">
            <span className="text-gray-400">Ended:</span>
            <span className="text-white ml-2">{formatDate(session.endedAt)}</span>
          </div>
        )}
      </div>

      {/* Stats (if available) */}
      {session.stats && (
        <div className="bg-gray-700 rounded p-3 mb-4">
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div>
              <span className="text-gray-400">Audience:</span>
              <span className="text-white ml-2 font-semibold">{session.stats.totalAudience}</span>
            </div>
            <div>
              <span className="text-gray-400">Characters:</span>
              <span className="text-white ml-2 font-semibold">{session.stats.charactersCount}</span>
            </div>
            <div>
              <span className="text-gray-400">Votes:</span>
              <span className="text-white ml-2 font-semibold">{session.stats.votesCount}</span>
            </div>
            <div>
              <span className="text-gray-400">Challenges:</span>
              <span className="text-white ml-2 font-semibold">{session.stats.challengesCount}</span>
            </div>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex flex-col gap-2">
        {/* Configure Sheets button for DRAFT and SETUP */}
        {(session.status === 'DRAFT' || session.status === 'SETUP') && (
          <button
            onClick={() => navigate(`/admin/session/${session.id}`)}
            className="w-full bg-purple-600 hover:bg-purple-700 text-white font-semibold py-2 px-4 rounded transition text-sm"
          >
            ⚙️ Configure Sheets
          </button>
        )}

        {/* Status-specific actions */}
        <div className="flex gap-2">
          {session.status === 'DRAFT' && (
            <>
              <button
                onClick={() => onActivate(session.id)}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white font-semibold py-2 px-4 rounded transition text-sm"
              >
                Activate
              </button>
              <button
                onClick={() => onDelete(session.id)}
                className="bg-red-600 hover:bg-red-700 text-white font-semibold py-2 px-4 rounded transition text-sm"
              >
                Delete
              </button>
            </>
          )}

          {session.status === 'SETUP' && (
            <button
              onClick={() => onActivate(session.id)}
              className="flex-1 bg-green-600 hover:bg-green-700 text-white font-semibold py-2 px-4 rounded transition text-sm"
            >
              Activate
            </button>
          )}

          {session.status === 'ACTIVE' && (
            <>
              <button
                onClick={() => navigate(`/admin/session/${session.id}`)}
                className="flex-1 bg-purple-600 hover:bg-purple-700 text-white font-semibold py-2 px-4 rounded transition text-sm"
              >
                🎮 Manage Show
              </button>
              <button
                onClick={() => onClose(session.id)}
                className="bg-red-600 hover:bg-red-700 text-white font-semibold py-2 px-4 rounded transition text-sm"
              >
                Close
              </button>
            </>
          )}

          {session.status === 'COMPLETED' && (
            <button
              onClick={() => navigate(`/admin/session/${session.id}`)}
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded transition text-sm"
            >
              View Details
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
